// pricing-summary — plain-language one-line sentences for the pricing
// surface's compact rows (fees, taxes, discounts) and payment sections
// (Buchung step 1). Pure functions: no DOM, no Alpine — registered as the
// `$pricingSummary` magic in alpine-core-registrations.ts, which feeds the
// localized phrase templates from the nearest [data-pricing-phrases] JSON
// (server-rendered from locales/{lang}/pricing_config.json `summary.*` by
// templates/pages/property_pricing.templ's pricingSummaryPhrasesJSON).
//
// Robustness contract: every function tolerates missing/garbage rows and
// missing phrase keys (a missing phrase renders as an empty part, an unknown
// enum value falls back to its raw value) and never throws — these run
// inside Alpine x-text expressions on every keystroke.

import { formatISODate, nextVersions, sameRateValues, viewForToday, type RateVersion, type VersionedTax } from "./pricing-tax-versions";

export interface SummaryPhrases {
  sep: string;
  decimal: string;
  percent: string;
  scope: Record<string, string>;
  percent_of: string;
  percent_base: Record<string, string>;
  mandatory: string;
  optional: string;
  max_qty: string;
  from_nights: string;
  below_nights: string;
  lead_min: string;
  lead_max: string;
  tiers: string;
  /** Singular variant of `tiers`, used when n === 1 ("1 Staffel"). */
  tiers_one?: string;
  exclusive: string;
  tax_flat: string;
  tax_percent: string;
  tax_cap: string;
  /** "{count} saisonale Sätze" suffix on a tax row with seasonal rates. */
  tax_seasonal: string;
  /** Singular variant of `tax_seasonal` (n === 1). */
  tax_seasonal_one?: string;
  /** "Preisstaffel, {count} Stufen" — price_bracket step count. */
  tax_bracket: string;
  /** Singular variant of `tax_bracket` (n === 1). */
  tax_bracket_one?: string;
  /** "darüber +{add} je {every}" — an escalating top. */
  tax_bracket_escalate: string;
  /** "max. {amount}" — the escalation's cap. */
  tax_bracket_max: string;
  tax_base_net: string;
  tax_base_gross: string;
  /** Percent/bracket rows whose share split leaves out exempt children. */
  tax_share_excl: string;
  /** "inkl. {list}" — non-lodging categories in a price-based tax's base. */
  tax_base_incl?: string;
  /** "inkl. {list} (auch gebuchte)" — same, with booked extras counted. */
  tax_base_incl_booked?: string;
  /** "inkl. gebuchter Zusatzangebote" — lodging only, booked extras counted. */
  tax_base_booked_only?: string;
  /** "ab {date}: {value}" — the next planned rate change. */
  tax_next_change?: string;
  /** "ab {date}: neue Staffel" — the next change replaces the bracket table. */
  tax_next_change_table?: string;
  /** "ab {date}: geänderte Sätze" — the next change leaves the main value as is. */
  tax_next_change_other?: string;
  /** "(+{n})" — further planned changes. */
  tax_next_change_more?: string;
  /** Shown while a planned rate change is incomplete. */
  versions_error_summary?: string;
  deposit: Record<string, string>;
  prepay_off: string;
  prepay: string;
  prepay_tiers: string;
  installment: string;
  /** Singular variant of `installment`, used when n === 1 ("1 Tag"). */
  installment_one?: string;
  payment_base: Record<string, string>;
  rest: string;
  /** "Rest {n} Tage vor Anreise" — the ③ row with a non-zero offset. */
  rest_before?: string;
  rest_before_one?: string;
  /** Late-booking rule (installments past their date move to the booking):
   *  `late_all` when even the earliest-ending row is past ⇒ everything is
   *  due at booking; `late_some` when only some installments move. */
  late_all?: string;
  late_all_one?: string;
  late_some?: string;
  late_some_one?: string;
  /** "Belegung & Kinder" example-sentence phrases; see pricing-occupancy.ts. */
  occ?: import("./pricing-occupancy").OccPhrases;
  /** Cancellation-terms tab (Booking R6, Task 8) — see cancellationSummary. */
  cancellation_free: string;
  /** Singular variant of `cancellation_free`, used when n === 1 ("1 Tag"). */
  cancellation_free_one?: string;
  cancellation_tier: string;
  /** Singular variant of `cancellation_tier`, used when n === 1 ("1 Tag"). */
  cancellation_tier_one?: string;
  cancellation_no_show: string;
  /** Stay-rules card (Booking R8) — see stayRulesSummary. min/max are NIGHTS,
   *  lead/horizon are DAYS; each has a `_one` singular variant. */
  stay_rules_min: string;
  stay_rules_min_one?: string;
  stay_rules_max: string;
  stay_rules_max_one?: string;
  stay_rules_lead: string;
  stay_rules_lead_one?: string;
  stay_rules_horizon: string;
  stay_rules_horizon_one?: string;
  /** Shown when no stay rule is set at all. */
  stay_rules_none: string;
  /** Base weekday gates (R8 Phase 2) — `{days}` is a joined list of names
   *  from `weekday_short`, rendered Monday-first (WEEKDAY_DISPLAY_ORDER). */
  stay_rules_arrival_days: string;
  stay_rules_departure_days: string;
  /** Seasonal-exceptions count clause; `_one` variant for exactly 1. */
  stay_rules_exceptions: string;
  stay_rules_exceptions_one?: string;
  /** R8 Phase 3 — see stayRulesGapHint / stayRulesCutoffSentence. */
  stay_rules_gap_dead?: string;
  stay_rules_gap_pms?: string;
  stay_rules_cutoff_today?: string;
  stay_rules_cutoff_tomorrow?: string;
  stay_rules_cutoff_days?: string;
  /** Short weekday names keyed by day abbreviation (sun/mon/tue/wed/thu/
   *  fri/sat) — a Record, not an array, because go-i18n message leaves must
   *  be strings; the locale JSON's `summary.weekday_short.<key>` nested
   *  object is the only representable shape (same convention as `deposit` /
   *  `percent_base` above). WEEKDAY_KEYS below maps bit VALUE (0=Sun…6=Sat)
   *  to the matching key. */
  weekday_short: Record<string, string>;
  /** Conditions block one-liner (conditionsSummary): shown when no
   *  condition is set. */
  cond_none?: string;
  /** Short date "{d}.{m}." (de) / "{m}/{d}" (en); {y} is also available. */
  cond_date?: string;
  cond_stay_range?: string;
  cond_stay_from?: string;
  cond_stay_to?: string;
  cond_booking_range?: string;
  cond_booking_from?: string;
  cond_booking_to?: string;
  cond_blackout?: string;
  cond_nights_min?: string;
  cond_nights_max?: string;
  cond_min_spend?: string;
  /** Keyed by RatePlanScope (flexible/non_refundable); "both" says nothing. */
  cond_rate_plan?: Record<string, string>;
  /** Combined-effect sentence (combinedEffectSummary): "{list}" of
   *  "<label> <value>" items joined by combo_plus. */
  combo_together?: string;
  /** "{item}" — best_of with a cap: the largest single discount. */
  combo_best?: string;
  /** "= {pct}" — the additive worst-case total. */
  combo_total?: string;
  /** "→ capped at {pct}" — shown when the total exceeds the cap. */
  combo_capped?: string;
  /** Separator between items (" + "). */
  combo_plus?: string;
  /** "· at most {pct} in total" — the cap when the total is unknown (a
   *  flat amount is involved, so the trim can't be shown as a percent). */
  combo_cap_limit?: string;
}

// WEEKDAY_KEYS maps a Weekdays bit VALUE (0=Sun…6=Sat, domain.Weekdays' own
// numbering) to its `weekday_short` phrase key.
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// WEEKDAY_DISPLAY_ORDER lists weekday VALUES (0=Sun…6=Sat) in Monday-first
// display order, so a weekday-mask summary clause reads naturally ("Mo, Mi,
// Fr") instead of bit order ("Mi, Fr, Mo"). Mirrors the templ side's
// weekdayChips table (property_pricing_stay_rules.templ) — same
// ordered-table trick, so the two never need to be derived from a naive
// 0..6 loop that would misread which bit is "Saturday".
const WEEKDAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export interface SummaryCtx {
  currency: string;
  phrases: SummaryPhrases;
  kindLabels?: Record<string, string>;
  inclusionLabels?: Record<string, string>;
  debtorLabels?: Record<string, string>;
  modelLabels?: Record<string, string>;
  /** {ServiceCategory: short label} for a tax row's taxed categories
   *  (taxServiceCatLabelsJSON in property_pricing.templ). */
  serviceCatLabels?: Record<string, string>;
  /** ISO date (Europe/Berlin, server-rendered): tax rows show the rates valid
   *  on it, plus the next planned rate change. Unset = the row's base values. */
  today?: string;
  /** Locale for dates in the "ab {date}" clause; default "de". */
  lang?: "de" | "en";
}

// Row shapes mirror the JSON tags of the Go view-models in
// templates/pages/property_pricing.templ (pricingFeeRowVM, pricingTaxRowVM,
// pricingDiscountRowVM, pricingPaymentVM).
export interface FeeRow {
  kind?: string;
  inclusion?: string;
  amount_type?: string;
  amount_euros?: string;
  percent?: string;
  percent_base?: string;
  scope?: string;
  max_quantity?: string;
}

export interface TaxRow {
  kind?: string;
  debtor?: string;
  model?: string;
  percent?: string;
  flat_amount_euros?: string;
  max_taxed_nights?: string;
  /** Yearly recurring seasonal rates; only the count is summarized. */
  seasonal_rates?: unknown[];
  percent_base?: string;
  /** price_bracket steps; only the count is summarized. */
  brackets?: unknown[];
  bracket_top?: string;
  escalation_add_euros?: string;
  escalation_every_euros?: string;
  escalation_max_euros?: string;
  exclude_age_exempt_from_share?: boolean;
  /** CSV of ServiceCategory values taxed on top of lodging (percent/bracket). */
  taxable_components?: string;
  include_booked_extras?: boolean;
  /** Scheduled rate changes (effective_from + the row's rate keys). */
  rate_versions?: RateVersion[];
}

export interface DiscountRow {
  kind?: string;
  amount_type?: string;
  amount_euros?: string;
  percent?: string;
  night_min?: string;
  lead_days_min?: string;
  lead_days_max?: string;
  tiers?: unknown[];
  exclusive_of_other_discounts?: boolean;
}

export interface PaymentAmount {
  amount_type?: string;
  amount_euros?: string;
  percent?: string;
}

export interface PaymentState {
  deposit?: PaymentAmount & { mode?: string; percent_base?: string; methods?: string[] };
  prepayment?: PaymentAmount & { enabled?: boolean; base?: string; los_tiers?: unknown[] };
  installments?: (PaymentAmount & { offset_days_before_arrival?: number | string })[];
}

// CancellationSummaryInput mirrors the JSON tags/shape of the Go
// pricingTermsData literal (templates/pages/property_pricing_terms.templ) —
// the terms tab's `terms` object plus its own `free_until_days`/`tiers`
// fields flattened into snake_case the way cancellationSummary's caller
// passes them (see that templ's `$pricingSummary.cancellation({...})` call).
export interface CancellationSummaryInput {
  free_cancellation_enabled?: boolean;
  free_until_days?: number | string;
  tiers?: { from_days?: number | string; percent?: string }[];
  no_show_percent?: string;
}

// formatEuros — the CLIENT-side twin of internal/money.FormatCents (Go).
// Renders a raw amount string (as typed into a pricing field) in German
// number format: '.' thousands separator, ',' decimal separator, two
// decimals, currency symbol (€ for EUR/empty, else the ISO code — matching
// internal/pricing/money.displaySymbol). Kept byte-compatible with
// FormatCents: integer-cents rounding (no float drift), '-' sign prefix, dot
// grouping.
//
// Input tolerance: the amount field accepts German ("50,50" / "1.234,56") or
// plain ("50.50") input. When a comma is present it is the decimal mark and
// dots are thousands separators (stripped); otherwise a dot is the decimal.
// An empty value renders empty; an unparseable value is returned untouched so
// the operator still sees what they typed.
export function formatEuros(raw: unknown, currency?: string): string {
  const s = String(raw ?? "").trim();
  if (s === "") return "";
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalized.replace(/\s/g, ""));
  if (!Number.isFinite(n)) return s;
  const cents = Math.round(Math.abs(n) * 100);
  const euros = Math.floor(cents / 100);
  const frac = cents % 100;
  const grouped = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const sym = !currency || currency === "EUR" ? "€" : currency;
  const sign = n < 0 && cents !== 0 ? "-" : "";
  return `${sign}${grouped},${String(frac).padStart(2, "0")} ${sym}`;
}

// fill replaces every {key} in tpl with vars[key]. A missing template
// renders "" (so a missing phrase drops that part); an unknown placeholder
// is left as-is.
export function fill(tpl: unknown, vars: Record<string, unknown>): string {
  if (typeof tpl !== "string") return "";
  return tpl.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k] ?? "") : m));
}

// lookup returns map[key], falling back to the raw key for unknown values.
function lookup(map: Record<string, string> | undefined, key: unknown): string {
  const k = String(key ?? "");
  if (k === "") return "";
  const v = map && typeof map === "object" ? map[k] : undefined;
  return typeof v === "string" && v !== "" ? v : k;
}

// plural picks the `_one` singular phrase when n is exactly 1 (and the
// variant exists), else the general phrase. Two forms cover en and de; the
// phrases are server-rendered per locale (pricingSummaryPhrasesJSON).
function plural(other: string, one: string | undefined, n: unknown): string {
  return Number(n) === 1 && one ? one : other;
}

function join(ph: SummaryPhrases, parts: unknown[]): string {
  const sep = typeof ph.sep === "string" ? ph.sep : " · ";
  return parts.filter((p): p is string => typeof p === "string" && p !== "").join(sep);
}

function stripSign(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .replace(/^[-−+]\s*/, "");
}

// pct renders a percent string ("7.5" / "7,5") in the locale's format
// ("7,5 %" de / "7.5%" en). An unparseable value is shown raw.
function pct(raw: unknown, ph: SummaryPhrases): string {
  const s = String(raw ?? "").trim();
  if (s === "") return "";
  const n = Number(s.replace(",", ".").replace(/\s/g, ""));
  const num = Number.isFinite(n)
    ? String(n).replace(".", typeof ph.decimal === "string" ? ph.decimal : ".")
    : s;
  return typeof ph.percent === "string" ? fill(ph.percent, { pct: num }) : num + "%";
}

function isPercentage(row: { amount_type?: string } | undefined): boolean {
  return row?.amount_type === "percentage";
}

function present(v: unknown): boolean {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function feeSummary(fee: FeeRow, ctx: SummaryCtx): string {
  return safe(() => {
    const ph = (ctx?.phrases || {}) as SummaryPhrases;
    const f = fee || {};
    const pctMode = isPercentage(f);
    const amount = pctMode
      ? fill(ph.percent_of, {
          pct: pct(f.percent, ph),
          base: lookup(ph.percent_base, f.percent_base),
        })
      : formatEuros(f.amount_euros, ctx?.currency);
    const optional = f.inclusion === "optional_upsell";
    return join(ph, [
      amount,
      pctMode ? "" : lookup(ph.scope, f.scope),
      optional ? ph.optional : ph.mandatory,
      optional && present(f.max_quantity)
        ? fill(ph.max_qty, { n: String(f.max_quantity).trim() })
        : "",
    ]);
  }, "");
}

export function taxSummary(tax: TaxRow, ctx: SummaryCtx): string {
  return safe(() => {
    const ph = (ctx?.phrases || {}) as SummaryPhrases;
    const base = (tax || {}) as TaxRow;
    const today = ctx?.today;
    const t = (today ? viewForToday(base as unknown as VersionedTax, today) : base) as TaxRow;
    let model: string;
    if (t.model === "flat_per_person_night") {
      model = fill(ph.tax_flat, { amount: formatEuros(t.flat_amount_euros, ctx?.currency) });
    } else if (t.model === "percent_of_rate") {
      model = fill(ph.tax_percent, { pct: pct(t.percent, ph) });
    } else if (t.model === "price_bracket") {
      const esc = t.bracket_top === "escalate";
      model = join(ph, [
        bracketCount(t, ph),
        esc && present(t.escalation_add_euros) && present(t.escalation_every_euros)
          ? fill(ph.tax_bracket_escalate, {
              add: formatEuros(t.escalation_add_euros, ctx?.currency),
              every: formatEuros(t.escalation_every_euros, ctx?.currency),
            })
          : "",
        esc && present(t.escalation_max_euros)
          ? fill(ph.tax_bracket_max, { amount: formatEuros(t.escalation_max_euros, ctx?.currency) })
          : "",
        t.percent_base === "gross" ? ph.tax_base_gross : ph.tax_base_net,
      ]);
    } else {
      model = lookup(ctx?.modelLabels, t.model);
    }
    return join(ph, [
      lookup(ctx?.debtorLabels, t.debtor),
      model,
      taxBaseClause(t, ph, ctx?.serviceCatLabels),
      Array.isArray(t.seasonal_rates) &&
      t.seasonal_rates.length > 0 &&
      (t.model === "percent_of_rate" || t.model === "flat_per_person_night")
        ? fill(plural(ph.tax_seasonal, ph.tax_seasonal_one, t.seasonal_rates.length), {
            count: t.seasonal_rates.length,
          })
        : "",
      t.exclude_age_exempt_from_share && (t.model === "percent_of_rate" || t.model === "price_bracket")
        ? ph.tax_share_excl
        : "",
      present(t.max_taxed_nights) ? fill(ph.tax_cap, { n: String(t.max_taxed_nights).trim() }) : "",
      today ? nextChangeClause(t, base, today, ph, ctx) : "",
    ]);
  }, "");
}

// bracketCount is "Preisstaffel, n Stufen" for a price_bracket row: the
// flat top is a visible row; an escalation isn't.
function bracketCount(t: TaxRow, ph: SummaryPhrases): string {
  const rows = Array.isArray(t.brackets) ? t.brackets.length : 0;
  const n = t.bracket_top === "escalate" ? rows : rows + 1;
  return fill(plural(ph.tax_bracket, ph.tax_bracket_one, n), { count: n });
}

// taxRateValue is ONE rate row's main value (the base row or a rate
// version, carrying the tax's `model`) for the drawer's rate-version list:
// the percent, the flat amount, or the bracket count. "" when blank.
export function taxRateValue(row: TaxRow, ctx: SummaryCtx): string {
  return safe(() => {
    const ph = (ctx?.phrases || {}) as SummaryPhrases;
    const t = (row || {}) as TaxRow;
    if (t.model === "percent_of_rate") return pct(t.percent, ph);
    if (t.model === "flat_per_person_night") return formatEuros(t.flat_amount_euros, ctx?.currency);
    if (t.model === "price_bracket") return bracketCount(t, ph);
    return "";
  }, "");
}

// nextChangeClause summarises the first planned rate change after `today`
// relative to the values valid today (`cur`): its new main value when that
// differs, "new brackets" for a price table, "changed rates" when only the
// secondary values (seasons, escalation) differ; "(+n)" for further ones.
function nextChangeClause(
  cur: TaxRow,
  base: TaxRow,
  today: string,
  ph: SummaryPhrases,
  ctx: SummaryCtx,
): string {
  const next = nextVersions(base as unknown as VersionedTax, today);
  if (next.length === 0) return "";
  const nv = next[0];
  const date = formatISODate(String(nv.effective_from ?? ""), ctx?.lang === "en" ? "en" : "de");
  let text: string;
  if (cur.model === "price_bracket") {
    text = sameRateValues(cur, { ...cur, ...nv })
      ? ""
      : fill(ph.tax_next_change_table, { date });
  } else {
    const key = cur.model === "flat_per_person_night" ? "flat_amount_euros" : "percent";
    const was = String(cur[key] ?? "").trim();
    const now = String(nv[key] ?? was).trim();
    if (key === "percent") {
      text = was !== now && now !== "" ? fill(ph.tax_next_change, { date, value: pct(now, ph) }) : "";
    } else {
      text =
        was !== now && now !== ""
          ? fill(ph.tax_next_change, { date, value: formatEuros(now, ctx?.currency) })
          : "";
    }
  }
  if (text === "") text = fill(ph.tax_next_change_other, { date });
  return next.length > 1 ? `${text} ${fill(ph.tax_next_change_more, { n: next.length - 1 })}` : text;
}

// taxBaseClause names what a price-based tax (percent/bracket) taxes beyond
// lodging, which is always in the base and so never listed. Flat taxes have
// no price base — no clause.
function taxBaseClause(t: TaxRow, ph: SummaryPhrases, labels: Record<string, string> | undefined): string {
  if (t.model !== "percent_of_rate" && t.model !== "price_bracket") return "";
  const cats = String(t.taxable_components ?? "")
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c !== "" && c !== "lodging");
  const booked = t.include_booked_extras === true;
  if (cats.length > 0) {
    const list = cats.map((c) => lookup(labels, c)).join(", ");
    return fill(booked ? ph.tax_base_incl_booked : ph.tax_base_incl, { list });
  }
  return booked && typeof ph.tax_base_booked_only === "string" ? ph.tax_base_booked_only : "";
}

export function discountSummary(d: DiscountRow, ctx: SummaryCtx): string {
  return safe(() => {
    const ph = (ctx?.phrases || {}) as SummaryPhrases;
    const r = d || {};
    const kind = r.kind || "";
    const surcharge = kind === "short_stay_surcharge";

    let amount: string;
    if (kind === "los_custom") {
      const n = Array.isArray(r.tiers) ? r.tiers.length : 0;
      amount = fill(plural(ph.tiers, ph.tiers_one, n), { n });
    } else {
      const abs = isPercentage(r)
        ? pct(stripSign(r.percent), ph)
        : formatEuros(stripSign(r.amount_euros), ctx?.currency);
      amount = abs === "" ? "" : (surcharge ? "+" : "−") + abs;
    }

    let condition = "";
    if (kind === "los_weekly" || kind === "los_monthly") {
      const n = present(r.night_min)
        ? String(r.night_min).trim()
        : kind === "los_weekly"
          ? "7"
          : "28";
      condition = fill(ph.from_nights, { n });
    } else if (kind === "early_bird" && present(r.lead_days_min)) {
      condition = fill(ph.lead_min, { n: String(r.lead_days_min).trim() });
    } else if (kind === "last_minute" && present(r.lead_days_max)) {
      condition = fill(ph.lead_max, { n: String(r.lead_days_max).trim() });
    } else if (surcharge && present(r.night_min)) {
      condition = fill(ph.below_nights, { n: String(r.night_min).trim() });
    }

    return join(ph, [amount, condition, r.exclusive_of_other_discounts ? ph.exclusive : ""]);
  }, "");
}

export interface CombiningInput {
  policy?: string;
  cap_percent?: string;
  discounts?: (DiscountRow & { name?: string; disabled?: boolean })[];
  /** {kind: group} from pricing.DiscountGroupOf (server-rendered). */
  groups?: Record<string, string>;
  kindLabels?: Record<string, string>;
}

function pctNumber(raw: unknown): number | null {
  const s = String(raw ?? "").trim();
  if (s === "") return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? Math.abs(n) : null;
}

// euroNumber parses an amount field the way formatEuros does ("1.234,56" /
// "50.50"); null when blank or unparseable. Sign is dropped.
function euroNumber(raw: unknown): number | null {
  const s = stripSign(raw);
  if (s === "") return null;
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalized.replace(/\s/g, ""));
  return Number.isFinite(n) ? Math.abs(n) : null;
}

// round2 drops float noise (12.3 + 4.4 = 16.700000000000003).
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

type ComboValue = { pct: number | null; flat: number | null; flatRaw: string };

// rowWorstValue is a row's largest configured value: a percent (preferred —
// it can be summed and compared with the cap) or, failing that, a flat
// amount. A los_custom ladder uses its largest percent tier, else its
// largest flat tier. Values ≤ 0 don't count (null): a fresh row's "0".
function rowWorstValue(r: DiscountRow): ComboValue {
  const none: ComboValue = { pct: null, flat: null, flatRaw: "" };
  const pick = (rows: { amount_type?: string; percent?: string; amount_euros?: string }[]): ComboValue => {
    let best = none;
    for (const t of rows) {
      if (t?.amount_type === "percentage") {
        const v = pctNumber(t.percent);
        if (v !== null && v > 0 && (best.pct === null || v > best.pct)) best = { pct: v, flat: null, flatRaw: "" };
      } else if (best.pct === null) {
        const v = euroNumber(t?.amount_euros);
        if (v !== null && v > 0 && (best.flat === null || v > best.flat)) best = { pct: null, flat: v, flatRaw: String(t?.amount_euros ?? "") };
      }
    }
    return best;
  };
  if (r.kind === "los_custom") {
    return Array.isArray(r.tiers) ? pick(r.tiers as { amount_type?: string; percent?: string; amount_euros?: string }[]) : none;
  }
  return pick([r]);
}

/** R4's cap sentence (spec D11): worst case per group (largest configured
 *  value, conditions ignored). Shown when ≥ 2 groups have a discount
 *  (additive) or a cap is set; "" otherwise. Flat amounts are listed but
 *  not summed (the total is then omitted and the cap is stated neutrally). */
export function combinedEffectSummary(v: CombiningInput, ctx: SummaryCtx): string {
  return safe(() => {
    const ph = (ctx?.phrases || {}) as SummaryPhrases;
    const capRaw = pctNumber(v?.cap_percent);
    const cap = capRaw !== null && capRaw > 0 ? capRaw : null;
    type Item = ComboValue & { label: string };
    // rank: any percent beats any flat amount; within a kind, larger wins.
    const rank = (it: Item) => (it.pct !== null ? 1e12 + it.pct : (it.flat ?? 0));
    const perGroup = new Map<string, Item>();
    for (const r of Array.isArray(v?.discounts) ? v.discounts : []) {
      if (!r || r.disabled || r.exclusive_of_other_discounts) continue;
      const group = v.groups?.[r.kind ?? ""] ?? "";
      if (group === "" || group === "code") continue;
      const val = rowWorstValue(r);
      if (val.pct === null && val.flat === null) continue;
      const label = r.kind === "promotion" && r.name ? r.name : lookup(v.kindLabels, r.kind);
      const item: Item = { ...val, label };
      const cur = perGroup.get(group);
      if (!cur || rank(item) > rank(cur)) perGroup.set(group, item);
    }
    const items = [...perGroup.values()];
    if (items.length === 0) return "";
    const show = (it: Item) =>
      `${it.label} ${it.pct !== null ? pct(String(round2(it.pct)), ph) : formatEuros(it.flatRaw, ctx?.currency)}`;
    const capClause = (total: number | null) => {
      if (cap === null) return "";
      const capPct = pct(String(round2(cap)), ph);
      if (total === null) return " " + fill(ph.combo_cap_limit, { pct: capPct });
      return total > cap ? " " + fill(ph.combo_capped, { pct: capPct }) : "";
    };
    if ((v.policy ?? "best_of") === "additive_groups") {
      if (items.length < 2 && cap === null) return "";
      const list = items.map(show).join(ph.combo_plus ?? " + ");
      const total = items.every((it) => it.pct !== null) ? round2(items.reduce((s, it) => s + (it.pct ?? 0), 0)) : null;
      const totalClause = total !== null && items.length > 1 ? " " + fill(ph.combo_total, { pct: pct(String(total), ph) }) : "";
      return fill(ph.combo_together, { list }) + totalClause + capClause(total);
    }
    if (cap === null) return "";
    const best = items.reduce((a, b) => (rank(b) > rank(a) ? b : a));
    return fill(ph.combo_best, { item: show(best) }) + capClause(best.pct);
  }, "");
}

/** THE scalar keys of the conditions model — must equal Go's
 *  components.ConditionsScalarFields (golden-tested via
 *  templates/components/testdata/conditions_keys.json). */
export const CONDITIONS_SCALAR_KEYS = [
  "date_from",
  "date_to",
  "booking_from",
  "booking_to",
  "night_min",
  "night_max",
  "min_spend_euros",
  "rate_plan_scope",
] as const;

/** The conditions model (Go pages.pricingConditionsVM). */
export type ConditionsRow = Partial<Record<(typeof CONDITIONS_SCALAR_KEYS)[number], string | number>> & {
  blackouts?: { key?: string; from?: string; to?: string }[];
};

// shortDate renders an ISO date via cond_date ("23.12." / "12/23"); a
// non-ISO value is shown raw.
function shortDate(iso: unknown, ph: SummaryPhrases): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? "").trim());
  if (!m) return String(iso ?? "");
  return fill(ph.cond_date ?? "{d}.{m}.", { d: String(Number(m[3])), m: String(Number(m[2])), y: m[1] });
}

// rangeClause renders an optional, possibly one-sided date window.
function rangeClause(
  from: unknown,
  to: unknown,
  ph: SummaryPhrases,
  both?: string,
  onlyFrom?: string,
  onlyTo?: string,
): string {
  const f = present(from);
  const t = present(to);
  if (f && t) return fill(both, { from: shortDate(from, ph), to: shortDate(to, ph) });
  if (f) return fill(onlyFrom, { from: shortDate(from, ph) });
  if (t) return fill(onlyTo, { to: shortDate(to, ph) });
  return "";
}

/** The conditions block's collapsed one-liner ("Mind. 3 Nächte · Nicht
 *  23.12.–2.1. · Buchung bis 31.1.") or cond_none. Clause order: nights,
 *  stay dates, blackouts, booking window, min spend, rate plan.
 *  skipNightMin: the kind shows its own nights trigger; skipNightMax: the
 *  kind ignores night_max (short_stay_surcharge — the server drops it). */
export function conditionsSummary(
  c: ConditionsRow,
  ctx: SummaryCtx & { skipNightMin?: boolean; skipNightMax?: boolean },
): string {
  return safe(() => {
    const ph = (ctx?.phrases || {}) as SummaryPhrases;
    const r = c || {};
    const parts: string[] = [];
    if (!ctx?.skipNightMin && present(r.night_min)) parts.push(fill(ph.cond_nights_min, { n: String(r.night_min).trim() }));
    if (!ctx?.skipNightMax && present(r.night_max)) parts.push(fill(ph.cond_nights_max, { n: String(r.night_max).trim() }));
    parts.push(rangeClause(r.date_from, r.date_to, ph, ph.cond_stay_range, ph.cond_stay_from, ph.cond_stay_to));
    for (const b of Array.isArray(r.blackouts) ? r.blackouts : []) {
      if (present(b?.from) && present(b?.to)) {
        parts.push(fill(ph.cond_blackout, { from: shortDate(b.from, ph), to: shortDate(b.to, ph) }));
      }
    }
    parts.push(rangeClause(r.booking_from, r.booking_to, ph, ph.cond_booking_range, ph.cond_booking_from, ph.cond_booking_to));
    if (present(r.min_spend_euros)) {
      parts.push(fill(ph.cond_min_spend, { amount: formatEuros(r.min_spend_euros, ctx?.currency) }));
    }
    if (r.rate_plan_scope && r.rate_plan_scope !== "both") parts.push(lookup(ph.cond_rate_plan, r.rate_plan_scope));
    const out = join(ph, parts);
    return out !== "" ? out : (ph.cond_none ?? "");
  }, "");
}

function paymentAmount(
  a: PaymentAmount | undefined,
  base: string | undefined,
  ctx: SummaryCtx,
  ph: SummaryPhrases,
  withBase: boolean,
): string {
  if (!a) return "";
  if (!isPercentage(a)) return formatEuros(a.amount_euros, ctx?.currency);
  const p = pct(a.percent, ph);
  return withBase ? fill(ph.percent_of, { pct: p, base: lookup(ph.payment_base, base) }) : p;
}

// lateBookingPhrase spells out BalanceInstallments' clamp (schedule_cash.go):
// an installment whose date is already past at booking is due at booking.
// Booking n days or less before arrival, with n the SMALLEST offset of all
// rows, leaves nothing for later ⇒ the full amount; otherwise the LARGEST
// offset is where the first installment starts moving. "" when every row is
// due at arrival (offset 0) — nothing can move.
function lateBookingPhrase(offsets: number[], ph: SummaryPhrases): string {
  const min = Math.min(...offsets);
  const max = Math.max(...offsets);
  if (min > 0) return fill(plural(ph.late_all ?? "", ph.late_all_one, min), { n: min });
  if (max > 0) return fill(plural(ph.late_some ?? "", ph.late_some_one, max), { n: max });
  return "";
}

export function paymentSummaries(
  p: PaymentState,
  ctx: SummaryCtx,
): { deposit: string; prepayment: string; schedule: string } {
  const ph = (ctx?.phrases || {}) as SummaryPhrases;
  const pay = p || {};

  const deposit = safe(() => {
    const dep = pay.deposit || {};
    // P3 (D9): the ticked Kaution methods. A pre-P3 state without `methods`
    // falls back like domain.EffectiveKautionMethods (K1).
    const methods = Array.isArray(dep.methods)
      ? dep.methods
      : (dep.mode || "none") === "none"
        ? []
        : ["card_hold", "on_site"];
    if (methods.length === 0) return lookup(ph.deposit, "none");
    const amount = paymentAmount(dep, dep.percent_base, ctx, ph, true);
    const labels = methods.map((m) => lookup(ph.deposit, m)).join(", ");
    const tpl = ph.deposit && typeof ph.deposit === "object" ? ph.deposit.with_methods : undefined;
    return typeof tpl === "string" ? fill(tpl, { amount, methods: labels }) : join(ph, [amount, labels]);
  }, "");

  const prepayment = safe(() => {
    const pre = pay.prepayment || {};
    if (!pre.enabled) return ph.prepay_off || "";
    return join(ph, [
      fill(ph.prepay, { amount: paymentAmount(pre, pre.base, ctx, ph, true) }),
      Array.isArray(pre.los_tiers) && pre.los_tiers.length > 0 ? ph.prepay_tiers : "",
    ]);
  }, "");

  // The LAST installment is the "rest at arrival" row (③, it absorbs the
  // remainder — buildBalanceCharges ignores its amount), rendered by the
  // `rest` phrase rather than as an installment; the UI's ② repeater skips
  // it the same way (x-show="idx < payment.installments.length - 1").
  const schedule = safe(() => {
    const insts = Array.isArray(pay.installments) ? pay.installments : [];
    const parts = insts.slice(0, Math.max(0, insts.length - 1)).map((i) => {
      const n = i?.offset_days_before_arrival ?? "";
      return fill(plural(ph.installment, ph.installment_one, n), {
        amount: paymentAmount(i, undefined, ctx, ph, false),
        n,
      });
    });
    // Review fix round 2 (m-1): an empty `installments` array (owner
    // decision B's all-at-booking fallback, or any config where nothing is
    // left for arrival) must not append the "Rest bei Anreise" phrase —
    // there IS no ③ row to describe then (property_pricing.templ wraps it
    // in `<template x-if="payment.installments.length > 0">`, so it's not
    // even in the DOM). Appending it unconditionally produced the
    // contradiction the controller observed live: "② Raten vor Anreise —
    // Rest bei Anreise" shown under a 100%-at-booking plan whose own
    // timeline read "Bei Buchung: 100%".
    if (insts.length === 0) return join(ph, parts);
    const offsets = insts.map((i) => Math.max(0, Number(i?.offset_days_before_arrival) || 0));
    const restOffset = offsets[offsets.length - 1];
    const rest =
      restOffset > 0 ? fill(plural(ph.rest_before ?? ph.rest, ph.rest_before_one, restOffset), { n: restOffset }) : ph.rest;
    return join(ph, [...parts, rest, lateBookingPhrase(offsets, ph)]);
  }, "");

  return { deposit, prepayment, schedule };
}

// cancellationSummary composes the terms tab's plain-language ladder
// sentence, e.g. "Kostenlos bis 15 Tage vor Anreise · ab 14 Tagen 50 % ·
// ab 3 Tagen 90 % · Nichtanreise 90 %". Every phrase is server-rendered
// (pricingSummaryPhrasesJSON's summary.cancellation_* keys) — never built
// from a hardcoded German/English string here, same contract as
// feeSummary/taxSummary/discountSummary/paymentSummaries above.
export function cancellationSummary(v: CancellationSummaryInput, phrases: SummaryPhrases): string {
  return safe(() => {
    const ph = (phrases || {}) as SummaryPhrases;
    const input = v || {};
    const parts: string[] = [];

    if (input.free_cancellation_enabled) {
      const n = input.free_until_days ?? "";
      parts.push(fill(plural(ph.cancellation_free, ph.cancellation_free_one, n), { n }));
    }

    for (const tier of Array.isArray(input.tiers) ? input.tiers : []) {
      const n = tier?.from_days ?? "";
      parts.push(
        fill(plural(ph.cancellation_tier, ph.cancellation_tier_one, n), {
          n,
          pct: pct(tier?.percent, ph),
        }),
      );
    }

    if (present(input.no_show_percent)) {
      parts.push(fill(ph.cancellation_no_show, { pct: pct(input.no_show_percent, ph) }));
    }

    return join(ph, parts);
  }, "");
}

// StayRulesSummaryInput mirrors the snake_case fields the stay-rules card's
// `$pricingSummary.stayRules({...})` call passes (see
// property_pricing_stay_rules.templ). Every field is optional; an unset field
// contributes no clause. min/max are NIGHTS, lead/horizon are DAYS.
export interface StayRulesSummaryInput {
  min_nights?: number | string;
  max_nights?: number | string;
  min_lead_days?: number | string;
  booking_horizon_days?: number | string;
  /** Base weekday gates (R8 Phase 2) — Weekdays bitmask, 0=Sun…6=Sat. */
  arrival_days?: number;
  departure_days?: number;
  /** Seasonal exceptions — only `label` is read (for the {labels} list). */
  exceptions?: { label: string }[];
}

// weekdayMaskNames renders a Weekdays bitmask as a Monday-first, comma-joined
// list of names from `weekday_short`. Returns "" for an unset/unparsable
// mask or a missing phrase map.
function weekdayMaskNames(mask: unknown, names: Record<string, string> | undefined): string {
  const m = Number(mask);
  if (!Number.isFinite(m) || m <= 0 || !names || typeof names !== "object") return "";
  const out: string[] = [];
  for (const d of WEEKDAY_DISPLAY_ORDER) {
    if (m & (1 << d)) out.push(names[WEEKDAY_KEYS[d]] ?? "");
  }
  return out.filter(Boolean).join(", ");
}

// stayRulesSummary composes the stay-rules card's plain-language sentence,
// e.g. "Mindestens 2 Nächte · höchstens 28 Nächte · frühestens 1 Tag im
// Voraus buchbar · buchbar bis 540 Tage im Voraus". Each clause is dropped
// when its field is empty; when nothing is set it returns the "no
// restrictions" phrase. Every phrase is server-rendered
// (pricingSummaryPhrasesJSON's summary.stay_rules_* keys), same contract as
// cancellationSummary above.
export function stayRulesSummary(v: StayRulesSummaryInput, phrases: SummaryPhrases): string {
  return safe(() => {
    const ph = (phrases || {}) as SummaryPhrases;
    const input = v || {};
    const parts: string[] = [];

    if (present(input.min_nights)) {
      const n = input.min_nights;
      parts.push(fill(plural(ph.stay_rules_min, ph.stay_rules_min_one, n), { n }));
    }
    if (present(input.max_nights)) {
      const n = input.max_nights;
      parts.push(fill(plural(ph.stay_rules_max, ph.stay_rules_max_one, n), { n }));
    }
    if (present(input.min_lead_days)) {
      const n = input.min_lead_days;
      parts.push(fill(plural(ph.stay_rules_lead, ph.stay_rules_lead_one, n), { n }));
    }
    if (present(input.booking_horizon_days)) {
      const n = input.booking_horizon_days;
      parts.push(fill(plural(ph.stay_rules_horizon, ph.stay_rules_horizon_one, n), { n }));
    }

    const arrivalDays = weekdayMaskNames(input.arrival_days, ph.weekday_short);
    if (arrivalDays) {
      parts.push(fill(ph.stay_rules_arrival_days, { days: arrivalDays }));
    }
    const departureDays = weekdayMaskNames(input.departure_days, ph.weekday_short);
    if (departureDays) {
      parts.push(fill(ph.stay_rules_departure_days, { days: departureDays }));
    }

    const exceptions = Array.isArray(input.exceptions) ? input.exceptions : [];
    if (exceptions.length > 0) {
      const count = exceptions.length;
      const labels = exceptions
        .map((e) => (e && typeof e.label === "string" ? e.label.trim() : ""))
        .filter(Boolean)
        .join(", ");
      parts.push(
        fill(plural(ph.stay_rules_exceptions, ph.stay_rules_exceptions_one, count), { count, labels }),
      );
    }

    if (parts.length === 0) return typeof ph.stay_rules_none === "string" ? ph.stay_rules_none : "";
    return join(ph, parts);
  }, "");
}

// StayRulesEditorState is the stay-rules card's Alpine `stayRules` object
// (property_pricing_stay_rules.templ's stayRulesXData) — the R8 Phase 3
// hints read it directly. Numbers arrive as input strings.
export interface StayRulesEditorState {
  pmsMinOverride?: boolean;
  gapFill?: boolean;
  minGapNights?: number | string;
  minNights?: number | string;
  minLeadDays?: number | string;
  cutoffTime?: string;
  exceptions?: { label: string; minNights?: number | string }[];
}

// stayRulesGapHint is the gap-fill card's advisory line (never a save error —
// spec rev 4): with the override on, warn when no window's minimum exceeds
// N (the setting can never apply); with it off, say it depends on Smoobu's
// per-date minimum. A blank base minimum means 1; an exception without a
// minimum inherits the base.
export function stayRulesGapHint(v: StayRulesEditorState, phrases: SummaryPhrases): string {
  return safe(() => {
    const ph = (phrases || {}) as SummaryPhrases;
    if (!v?.gapFill) return "";
    const n = Math.max(1, Number(v.minGapNights) || 1);
    if (!v.pmsMinOverride) return fill(ph.stay_rules_gap_pms, { n });
    const base = present(v.minNights) ? Number(v.minNights) : 1;
    const mins = [base, ...(v.exceptions ?? []).map((e) => (present(e?.minNights) ? Number(e.minNights) : base))];
    const max = Math.max(...mins);
    return n >= max ? fill(ph.stay_rules_gap_dead, { max, n }) : "";
  }, "");
}

// stayRulesCutoffSentence spells out the booking cutoff relative to the
// minimum lead: "Anreise heute/morgen/in N Tagen bis {time} buchbar".
export function stayRulesCutoffSentence(v: StayRulesEditorState, phrases: SummaryPhrases): string {
  return safe(() => {
    const ph = (phrases || {}) as SummaryPhrases;
    const time = (v?.cutoffTime ?? "").trim();
    if (!time) return "";
    const lead = Math.max(0, Number(v.minLeadDays) || 0);
    if (lead === 0) return fill(ph.stay_rules_cutoff_today, { time });
    if (lead === 1) return fill(ph.stay_rules_cutoff_tomorrow, { time });
    return fill(ph.stay_rules_cutoff_days, { n: lead, time });
  }, "");
}
